/**
 * Pure, client-safe models for the area blocks on a spot page (forecast areas spec, section 10.4):
 * which area board a sport lens reads, the one-line verdict, the coastal "Better nearby" list and
 * the mountain "Elsewhere in {Area} today" list. The server renders them for the primary sport;
 * BetterNearbyClient re-runs the same functions when the sport lens changes.
 */
import { betterNearbyRows, type AreaRow } from "@/lib/areas/row-model";
import { buildSpotVerdictLine, type SpotVerdictLine } from "@/lib/areas/spot-verdict";
import { isAreaBoardSport, type AreaBoardSport, type SurfDifficulty } from "@/lib/areas/types";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import { ELSEWHERE_SCORE_GAP } from "@/lib/companion/decision";

/** How many same-area rows the coastal block lists. */
export const COASTAL_NEARBY_LIMIT = 3;
/** How many rows the mountain block lists. */
export const MOUNTAIN_ELSEWHERE_LIMIT = 5;
/** Other members a locality needs before the coastal block is named after the town. */
export const LOCALITY_MIN_OTHERS = 2;

/**
 * The area board a spot page sport lens reads, or null when the lens has no generic area board
 * (named paddle profiles, sailing variants and unknown keys). Swimming has two internal keys.
 */
export function areaBoardSportForLens(lens: string): AreaBoardSport | null {
  const key = lens === "swim" ? "swimming" : lens;
  return isAreaBoardSport(key) ? key : null;
}

/** Freshness of a row for the verdict line: a ranked row is today's published score. */
export function rowFreshness(row: AreaRow | null): "updated-today" | "stale" | "missing" {
  if (!row || row.score10 === null) return "missing";
  if (row.status === "ranked") return "updated-today";
  return row.status === "stale" ? "stale" : "missing";
}

export type AreaLensRows = Readonly<{
  sport: AreaBoardSport;
  rows: readonly AreaRow[];
  stale: boolean;
}>;

export type SpotAreaFacts = Readonly<{
  slug: string;
  difficulty: SurfDifficulty | null;
  spotType: string | null;
  locality: string | null;
}>;

/**
 * Same-area rows that beat this spot by the Companion gap, best first, never red-flagged. The
 * comparison base is this spot's own score: its ranked score, or its last published score when
 * its row is stale. Returns null when there is no base to compare against (this spot is not on
 * the board, or its row is withheld, closed or unscored), so callers make no "better" claim.
 */
export function rowsThatBeatSpot(data: AreaLensRows, slug: string): readonly AreaRow[] | null {
  const own = data.rows.find((row) => row.slug === slug) ?? null;
  if (!own || own.score10 === null) return null;
  const candidates = betterNearbyRows({ rows: data.rows, snapshotStale: data.stale }, slug, { limit: data.rows.length });
  if (own.status === "ranked") return candidates;
  if (own.status !== "stale") return null;
  const base = own.score10;
  return candidates.filter((row) => row.score10 !== null && row.score10 >= base + ELSEWHERE_SCORE_GAP - 1e-9);
}

/** Ranked, non red-flagged rows other than this spot, best first: the list when no base exists. */
function otherRankedRows(data: AreaLensRows, slug: string): readonly AreaRow[] {
  if (data.stale) return [];
  return data.rows.filter((row) => row.slug !== slug && row.status === "ranked" && row.score10 !== null
    && row.safetyState !== "flagged-red");
}

/** This spot's local hour, for the verdict line (computed once on the server, then in an effect). */
export function localHourIn(timezone: string | null, now: Date = new Date()): number {
  try {
    const hour = new Intl.DateTimeFormat("en-GB", { timeZone: timezone ?? "UTC", hour: "2-digit", hour12: false }).format(now);
    return Number(hour) % 24;
  } catch {
    return now.getUTCHours();
  }
}

/**
 * The verdict line for this spot under the current lens, or null when the spot is not on the
 * lens's board (so there is no published row to speak for). Window times are never printed here:
 * the line links to the spot's own window instead (spec 5.5 and 6.2). The rows it may name are
 * the same rows the Better nearby list shows, so the line and the list never disagree.
 */
export function spotVerdictForLens(
  data: AreaLensRows,
  spot: SpotAreaFacts,
  localHour: number,
): SpotVerdictLine | null {
  const own = data.rows.find((row) => row.slug === spot.slug) ?? null;
  if (!own) return null;
  const beating = rowsThatBeatSpot(data, spot.slug);
  const better = (beating ?? otherRankedRows(data, spot.slug)).slice(0, COASTAL_NEARBY_LIMIT);
  return buildSpotVerdictLine({
    sport: data.sport,
    score10: own.status === "ranked" || own.status === "stale" ? own.score10 : null,
    freshness: data.stale ? "stale" : rowFreshness(own),
    safetyState: own.safetyState,
    difficulty: spot.difficulty,
    spotType: spot.spotType,
    betterNearby: better,
    windowLabel: null,
    localHour,
  });
}

export type CoastalNearbyModel = Readonly<{
  /** "Other Newquay beaches today", "Better nearby" or "Elsewhere in Cornwall today". */
  heading: string;
  /**
   * locality: the town's other beaches that beat this spot; better: same-area rows that beat it;
   * elsewhere: this spot has no score to compare against, so the area's ranked rows with no
   * "better" claim.
   */
  mode: "locality" | "better" | "elsewhere";
  rows: readonly AreaRow[];
  /** Copy for an empty list. */
  emptyText: string;
}>;

/**
 * Coastal block (spec 10.4 item 7): up to three same-area rows that beat this spot for the
 * current sport. When the spot's town holds at least two other members on this board, the block
 * is named after the town and lists only that town's beaches that beat it. Red-flagged rows are
 * never listed (betterNearbyRows excludes them).
 */
export function coastalNearbyModel(
  data: AreaLensRows,
  spot: SpotAreaFacts,
  names: Readonly<{ spotName: string; areaName: string }>,
): CoastalNearbyModel {
  const beating = rowsThatBeatSpot(data, spot.slug);
  const pool = beating ?? otherRankedRows(data, spot.slug);
  const awaiting = `Scores for ${names.areaName} are awaiting today's update, so no comparison is shown.`;
  const town = spot.locality;
  const townOthers = town ? data.rows.filter((row) => row.slug !== spot.slug && row.locality === town).length : 0;
  if (town && townOthers >= LOCALITY_MIN_OTHERS) {
    return {
      heading: data.stale ? `Other ${town} beaches` : `Other ${town} beaches today`,
      mode: "locality",
      rows: pool.filter((row) => row.locality === town).slice(0, COASTAL_NEARBY_LIMIT),
      emptyText: data.stale
        ? awaiting
        : beating
          ? `No other ${town} beach beats ${names.spotName} by a clear margin today.`
          : `No other ${town} beach has a score yet today.`,
    };
  }
  if (!beating) {
    return {
      heading: data.stale ? `Elsewhere in ${names.areaName}` : `Elsewhere in ${names.areaName} today`,
      mode: "elsewhere",
      rows: pool.slice(0, COASTAL_NEARBY_LIMIT),
      emptyText: data.stale ? awaiting : `No other spot in ${names.areaName} has a score yet today.`,
    };
  }
  return {
    heading: "Better nearby",
    mode: "better",
    rows: beating.slice(0, COASTAL_NEARBY_LIMIT),
    emptyText: data.stale
      ? awaiting
      : `Nothing else in ${names.areaName} beats ${names.spotName} by a clear margin today.`,
  };
}

export type MountainElsewhereModel = Readonly<{
  heading: string;
  rows: readonly AreaRow[];
  /** This resort's rank in the area today, when it is ranked. */
  ownRank: number | null;
  rankedCount: number;
  emptyText: string;
}>;

/** Mountain block: the area's ranked list for the lens sport, top five, this resort left out. */
export function mountainElsewhereModel(
  data: AreaLensRows,
  spot: Pick<SpotAreaFacts, "slug">,
  areaName: string,
): MountainElsewhereModel {
  const ranked = data.stale ? [] : data.rows.filter((row) => row.status === "ranked" && row.score10 !== null);
  const own = ranked.find((row) => row.slug === spot.slug) ?? null;
  return {
    // A stale board makes no "today" claim (the snapshot, or this spot's own row, is not current).
    heading: data.stale ? `Elsewhere in ${areaName}` : `Elsewhere in ${areaName} today`,
    rows: ranked.filter((row) => row.slug !== spot.slug).slice(0, MOUNTAIN_ELSEWHERE_LIMIT),
    ownRank: own?.rank ?? null,
    rankedCount: ranked.length,
    emptyText: data.stale
      ? `Scores for ${areaName} are awaiting today's update, so no ranking is shown.`
      : `No other resort in ${areaName} has a score today.`,
  };
}

/**
 * "72" for a ranked row; "Updating" for a stale one (its board entry is not today's current
 * score, so its number is withheld, as on the board and the spot page); else its published label
 * ("Closed") or "No score".
 */
export function areaRowScoreText(row: AreaRow): string {
  if (row.score10 !== null && row.status === "ranked") return String(scoreOutOf100(row.score10));
  if (row.status === "stale") return "Updating";
  if (row.status === "withheld" || row.status === "closed") return row.label ?? "Closed";
  return "No score";
}
